import { faEnvelope, faIdCard, faLock, faSignature } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import cn from 'classnames';
import { getCurrentUser, registerUser, loginUser } from '@redux/features/authSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { validateField } from 'helpers/validateField';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import styles from 'pages/Auth/Auth.scss';

export const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    surname: '',
    role: 1,
  });
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isAuthenticated, error, isLoading, user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAuthenticated && !user) {
      dispatch(getCurrentUser());
    }
  }, [isAuthenticated]);

  const getRedirectPath = useCallback(() => '/locations/choose', []);

  useEffect(() => {
    if (isAuthenticated && user && !isLoading) {
      const redirectPath = getRedirectPath();
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, isLoading, navigate, getRedirectPath]);

  useEffect(() => {
    if (error) {
      dispatch(
        addNotification({
          id: Date.now(),
          title: error,
          type: 'error',
        })
      );
    }
  }, [error, dispatch]);

  useEffect(() => {
    setFormData({ email: '', password: '', name: '', surname: '', role: 1 });
    setErrors({});
    setHasSubmitted(false);
    setIsSubmitting(false);
  }, [isLogin]);

  const validateForm = useCallback((data, isLoginForm) => {
    const newErrors = {
      email: validateField('email', data.email),
      password: validateField('password', data.password),
    };

    if (!isLoginForm) {
      newErrors.name = validateField('name', data.name);
      newErrors.surname = validateField('name', data.surname);
    }

    return newErrors;
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setFormData((prev) => ({ ...prev, [name]: value }));

      if (hasSubmitted) {
        const fieldError = validateField(name === 'surname' ? 'name' : name, value);
        setErrors((prev) => ({ ...prev, [name]: fieldError }));

        const allErrors = { ...errors, [name]: fieldError };
        const hasErrors = Object.values(allErrors).some((err) => err);
        setIsFormValid(!hasErrors);
      }
    },
    [hasSubmitted, errors]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (isSubmitting) return;

      setIsSubmitting(true);
      setHasSubmitted(true);

      const newErrors = validateForm(formData, isLogin);
      setErrors(newErrors);

      const hasErrors = Object.values(newErrors).some((error) => error);
      setIsFormValid(!hasErrors);

      if (!hasErrors) {
        try {
          if (isLogin) {
            await dispatch(
              loginUser({
                email: formData.email,
                password: formData.password,
              })
            ).unwrap();
          } else {
            await dispatch(registerUser(formData)).unwrap();
          }
        } catch (error) {
          console.error('Authentication error:', error);
        }
      }

      setIsSubmitting(false);
    },
    [dispatch, formData, isLogin, isSubmitting, validateForm]
  );

  const toggleAuthMode = useCallback(() => {
    setIsLogin((prev) => !prev);
  }, []);

  if (isLoading && isAuthenticated) {
    return (
      <section className={styles.auth}>
        <div className={styles.loading}>Завантаження...</div>
      </section>
    );
  }

  return (
    <section className={styles.auth}>
      <form className={cn(styles.form, { [styles.active]: !isLogin })} onSubmit={handleSubmit}>
        <h1 className={styles.title}>{isLogin ? 'Вхід' : 'Створити акаунт'}</h1>
        {!isLogin && (
          <>
            <Input
              value={formData.surname}
              name='surname'
              placeholder='Прізвище'
              icon={faIdCard}
              errorText={errors.surname}
              onChange={handleChange}
              invalid={!!errors.surname}
              disabled={isSubmitting}
            />
            <Input
              value={formData.name}
              name='name'
              placeholder="Ім'я"
              icon={faSignature}
              errorText={errors.name}
              onChange={handleChange}
              invalid={!!errors.name}
              disabled={isSubmitting}
            />
          </>
        )}
        <Input
          value={formData.email}
          name='email'
          placeholder='Пошта'
          icon={faEnvelope}
          errorText={errors.email}
          onChange={handleChange}
          invalid={!!errors.email}
          disabled={isSubmitting}
        />
        <Input
          value={formData.password}
          name='password'
          placeholder='Пароль'
          icon={faLock}
          type='password'
          errorText={errors.password}
          onChange={handleChange}
          invalid={!!errors.password}
          disabled={isSubmitting}
        />
        <Button
          className={styles.btn}
          text={isSubmitting ? 'Завантаження...' : isLogin ? 'Увійти' : 'Зареєструватись'}
          type='submit'
          disabled={isSubmitting || (hasSubmitted && !isFormValid)}
        />
        <p className={styles.authLink}>
          {isLogin ? 'Ще не маєте акаунту?' : 'Вже маєте акаунт?'}{' '}
          <span onClick={toggleAuthMode}>{isLogin ? 'Створити акаунт' : 'Увійти'}</span>
        </p>
      </form>
    </section>
  );
};
